import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Leaf } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useNurserySettings } from "../SettingsContext";
import { api, getErrorMessage } from "../api";
import { isDemoShortcutsAllowed } from "../lib/demoGuard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

const DEMO_ACCOUNTS = [
  { role: "admin", label: "Sign in as Admin", email: "admin@docblock.com", password: "Admin@123" },
  { role: "buyer", label: "Sign in as Buyer", email: "buyer.approved@example.com", password: "Buyer@123" },
];

export default function Login() {
  const { login } = useAuth();
  const { nurseryName, logoUrl } = useNurserySettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [demoBusy, setDemoBusy] = useState(null);
  // Hostname gate is evaluated once at mount — production domains must NEVER
  // show demo shortcuts even if /api/config erroneously reports demo_mode=true
  // (e.g. a packaged backend .env slipping through on deploy).
  const [hostAllowed] = useState(() => isDemoShortcutsAllowed());

  useEffect(() => {
    api.get("/config").then((r) => setDemoMode(!!r.data?.demo_mode)).catch(() => setDemoMode(false));
  }, []);

  const doLogin = async (em, pw) => {
    const u = await login(em, pw);
    navigate(u.role === "admin" ? "/admin/products" : "/catalog", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await doLogin(email, password);
    } catch (err) {
      toast.error(getErrorMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  const demoLogin = async (acct) => {
    setDemoBusy(acct.role);
    try {
      await doLogin(acct.email, acct.password);
    } catch (err) {
      toast.error(getErrorMessage(err, "Demo login failed"));
    } finally {
      setDemoBusy(null);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left panel — botanical hero */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1529924736887-7f19bef8bc97?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0f1f18]/45" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2 db-h">
            <span className="w-9 h-9 rounded-md bg-white/15 backdrop-blur-sm grid place-items-center overflow-hidden" data-testid="login-hero-logo">
              {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-contain" /> : <Leaf size={20} />}
            </span>
            <div>
              <div className="text-lg font-bold leading-tight truncate max-w-[220px]" data-testid="login-hero-name">{nurseryName}</div>
              <div className="text-xs opacity-80">Wholesale platform</div>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="db-h text-3xl xl:text-4xl font-bold tracking-tight">
              Rare plants, priced right for every partner.
            </h1>
            <p className="text-sm opacity-85 mt-3">
              A B2B catalogue with quantity tiers, country-specific pricing and per-buyer discounts.
            </p>
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 bg-[#F9F9F8]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="db-h mb-6">
            <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-[#5C665A] mt-1">Admin or approved buyer.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#5C665A]">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label className="text-xs text-[#5C665A]">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#1B4332] underline underline-offset-2 hover:no-underline" data-testid="link-forgot-password">
                  Forgot password?
                </Link>
              </div>
              <Input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10"
              />
            </div>
            <Button
              data-testid="login-submit"
              disabled={busy}
              className="w-full h-10 bg-[#2D6A4F] hover:bg-[#1B4332] text-white active:scale-[.98] transition-colors"
              type="submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>

          {/* Demo-only shortcuts: shown ONLY when the backend reports demo_mode=true
              AND the browser is on a preview/localhost host. Production domains
              never render this block regardless of what /api/config returns. */}
          {demoMode && hostAllowed && (
            <div
              data-testid="demo-shortcuts"
              className="mt-8 pt-4 border-t border-dashed border-[#D8D6D0]"
            >
              <div className="text-[11px] uppercase tracking-wider text-[#8A9088] mb-2">
                Testing shortcuts · demo only
              </div>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_ACCOUNTS.map((a) => (
                  <button
                    key={a.role}
                    type="button"
                    disabled={demoBusy !== null}
                    onClick={() => demoLogin(a)}
                    data-testid={`demo-login-${a.role}`}
                    className="text-xs px-3 py-2 rounded-md border border-[#D8D6D0] bg-white text-[#5C665A] hover:text-[#1B4332] hover:border-[#B7C0B4] disabled:opacity-50 transition-colors"
                  >
                    {demoBusy === a.role ? "…" : a.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-[#5C665A]">
            New buyer?{" "}
            <Link to="/register" data-testid="link-register" className="text-[#1B4332] font-medium underline underline-offset-2">
              Request an account
            </Link>
          </div>
          <div className="mt-2 text-xs text-[#8A9088]">
            By signing in you agree to our{" "}
            <Link to="/terms" data-testid="link-terms-login" className="underline underline-offset-2 hover:no-underline">
              General Terms and Conditions
            </Link>.
          </div>
        </form>
        <Toaster position="bottom-right" richColors closeButton />
      </div>
    </div>
  );
}
